import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { AppSettings, DEFAULT_SETTINGS, setGlobalSettings } from '../config/settings';
import { useUserSettings } from '../hooks/useUserSettings';

interface SettingsContextType {
  settings: AppSettings;
  isLoading: boolean;
  error: string | null;
  setSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
}

const SettingsContext = createContext<SettingsContextType>({
  settings: DEFAULT_SETTINGS,
  isLoading: false,
  error: null,
  setSettings: () => {}
});

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { settings, isLoading, error, setSettings } = useUserSettings();

  // Sync settings with global state for utility functions
  useEffect(() => {
    setGlobalSettings(settings);
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, isLoading, error, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}; 